import { Component } from '@theme/component';

/**
 * Fit tiers, tried in order until the table's min-content width fits its wrapper.
 * Tables never scroll sideways, never stack into cards and never become a carousel:
 * every table stays a table at the column's width.
 *
 * - (none): normal size, words wrap at spaces and hyphenate.
 * - tight: smaller type and padding.
 * - squeeze: tight, plus header words may break anywhere.
 * - break: last resort, body words may break anywhere too.
 * Numbers and prices are wrapped in a no-wrap span first, so "$2,300" or "2.9%"
 * never splits across lines at any tier.
 */
const FIT_TIERS = ['rte-table-wrapper--tight', 'rte-table-wrapper--squeeze', 'rte-table-wrapper--break'];

/** A price, percentage or other number token that must stay on one line. */
const NUMBER_TOKEN = /[$€£¥¢]?\d[\d.,:]*(?:%|¢|[kKmMbB]\b)?\+?(?:\/(?:mo|yr|month|year)\b)?/g;

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /**
   * The table each wrapper holds.
   * @type {WeakMap<Element, HTMLTableElement>}
   */
  #tables = new WeakMap();

  /**
   * Last wrapper width a fit was computed for, so the observer only refits on a real change.
   * @type {WeakMap<Element, number>}
   */
  #fittedAt = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLDivElement[]} */
    const wrappers = [];

    for (const node of this.querySelectorAll('table')) {
      const table = /** @type {HTMLTableElement} */ (node);
      const wrapper = this.#formatTable(table);
      if (!wrapper) continue;
      this.#protectNumbers(table);
      this.#tables.set(wrapper, table);
      wrappers.push(wrapper);
    }

    if (!wrappers.length) return;

    // Webfont metrics change a table's min-content width, so wait for the fonts to
    // settle before measuring. This also keeps the forced reflow off first paint.
    document.fonts.ready.then(() => {
      if (!this.isConnected) return;

      this.#resizeObserver = new ResizeObserver((entries) =>
        this.#fit(entries.map((entry) => /** @type {HTMLElement} */ (entry.target)))
      );

      // Observing fires the callback with the current size, which does the first fit.
      for (const wrapper of wrappers) this.#resizeObserver.observe(wrapper);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   * @returns {HTMLDivElement | undefined} The wrapper, if one was inserted.
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (!parent) return;
    parent.insertBefore(wrapper, table);
    wrapper.appendChild(table);
    return wrapper;
  }

  /**
   * Wraps every number token in the table's cells in a no-wrap span, so the squeeze
   * tier can break words without ever splitting a price or a percentage.
   * @param {HTMLTableElement} table
   */
  #protectNumbers(table) {
    const walker = document.createTreeWalker(table, NodeFilter.SHOW_TEXT);
    /** @type {Text[]} */
    const nodes = [];
    while (walker.nextNode()) {
      const node = /** @type {Text} */ (walker.currentNode);
      NUMBER_TOKEN.lastIndex = 0;
      if (NUMBER_TOKEN.test(node.data)) nodes.push(node);
    }

    for (const node of nodes) {
      const fragment = document.createDocumentFragment();
      let last = 0;
      for (const match of node.data.matchAll(NUMBER_TOKEN)) {
        const index = match.index ?? 0;
        if (index > last) fragment.append(node.data.slice(last, index));
        const span = document.createElement('span');
        span.className = 'rte-table-num';
        span.textContent = match[0];
        fragment.append(span);
        last = index + match[0].length;
      }
      if (last < node.data.length) fragment.append(node.data.slice(last));
      node.replaceWith(fragment);
    }
  }

  /**
   * Picks, for each wrapper, the loosest fit tier at which its table's min-content
   * width fits the wrapper.
   *
   * `.rte table` carries `width: 100% !important`, so measuring min-content needs an
   * `!important` of its own, or the table reports the wrapper's width back. All tables
   * are measured in one batch per tier (writes, then reads) so the page lays out once
   * per tier, not once per table.
   * @param {HTMLElement[]} wrappers
   */
  #fit(wrappers) {
    /** @type {{wrapper: HTMLElement, table: HTMLTableElement, width: number}[]} */
    let pending = [];
    for (const wrapper of wrappers) {
      const table = this.#tables.get(wrapper);
      const width = wrapper.clientWidth;
      // Hidden (a collapsed tab panel): nothing to measure until it has width.
      if (!table || !width || this.#fittedAt.get(wrapper) === width) continue;
      this.#fittedAt.set(wrapper, width);
      pending.push({ wrapper, table, width });
    }
    if (!pending.length) return;

    const measured = pending.map(({ table }) => table);
    for (const { wrapper, table } of pending) {
      wrapper.classList.remove(...FIT_TIERS);
      table.style.setProperty('width', 'min-content', 'important');
    }

    for (const tier of FIT_TIERS) {
      const widths = pending.map(({ table }) => table.getBoundingClientRect().width);
      pending = pending.filter((item, index) => (widths[index] ?? 0) > item.width + 1);
      if (!pending.length) break;
      for (const { wrapper } of pending) wrapper.classList.add(tier);
    }

    for (const table of measured) table.style.removeProperty('width');
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
